/** What a save — or its preview — does to the draft's days beyond the records core's own steps
 *  (plan §9.8: the removal protocol):
 *
 *  - **A removed day changed on the server meanwhile.** A removed day is sent as its six fields
 *    set to null, each compare-and-set from the value the editor loaded; when another editor
 *    changed one of them since, the server answers a drift (the preview's conflict, the apply's
 *    409) — the removal would delete their edit. The draft has no entry of that day to mark
 *    stale (removing a day reverts its own edits), so the day is BROUGHT BACK instead, where it
 *    was stored, as saved now (the edit context is refetched): the operator sees what changed
 *    and removes it again if they still mean to (`driftedRemovedDays`, `removedDaysDrifted`).
 *  - **What a removal takes with it**, in the server's sentences ("Removing day #9002 …"),
 *    names each day as its row does — by its stored place and title, "Day 2–3 · Ayutthaya" —
 *    never by its database id (`namedRemoval`, in the preview and the save's answer).
 *
 *  The new days' real ids (`created`, "day:-1" → 9123) are the core's step: the ledger's SAVED
 *  moves what the draft still holds of a new day — an edit made while the save was in flight —
 *  onto its real id, the day list following (`memberships`), in the same non-undoable step.
 *
 *  Pure over the draft state. */
import { DAYS_FIELD, removedDayIds } from '@/api/itineraryRecord';
import { joinAnd } from '@/features/catalog/words';
import type { DraftState, RecordKey } from '@/features/records/draft/changeSet';
import type { RecordApplyResult, RecordPreview } from '@/features/records/editor/types';

/** The saved days the draft removes (its day list's `from` less its `to`). */
export function draftRemovedDays(state: Pick<DraftState, 'entries'>, ownerRec: RecordKey): Set<number> {
  const days = state.entries[ownerRec]?.[DAYS_FIELD];
  return new Set(days ? removedDayIds(days.from, days.to) : []);
}

/** The removed days a drift names: a field of a day the draft removes, changed meanwhile. */
export function driftedRemovedDays(
  state: Pick<DraftState, 'entries'>,
  ownerRec: RecordKey,
  drifted: ReadonlyArray<{ entity: string; id: number }>,
): number[] {
  const removed = draftRemovedDays(state, ownerRec);
  return [...new Set(drifted.filter((d) => d.entity === 'day' && removed.has(d.id)).map((d) => d.id))];
}

/** The head of the server's sentence for what removing days takes with them: "Removing day
 *  #9002 “Ayutthaya”" / "Removing days #9002 “Ayutthaya”, #9005" — each day by its database id
 *  and stored title. */
const REMOVING_HEAD = /^Removing days? (#\d+(?: “[^”]*”)?(?:, #\d+(?: “[^”]*”)?)*) (?=also )/;
const REMOVED_DAY = /#(\d+)(?: “[^”]*”)?/g;

/** The server's removal sentence with each day named as the operator sees it — its stored
 *  row's name, `names` (`storedDayNames`): "Removing Day 2–3 · Ayutthaya also deletes …".
 *  Any other sentence, or one naming a day the stored itinerary does not hold, as it is. */
export function namedRemoval(sentence: string, names: ReadonlyMap<number, string>): string {
  const head = REMOVING_HEAD.exec(sentence);
  if (!head) return sentence;
  const named = [...head[1]!.matchAll(REMOVED_DAY)].map((m) => names.get(Number(m[1])));
  if (named.length === 0 || named.some((n) => n === undefined)) return sentence;
  return `Removing ${joinAnd(named as string[])} ${sentence.slice(head[0].length)}`;
}

/** A preview with the removal sentences named (`namedRemoval`): the effects' lines and the
 *  warnings. */
export function namedRemovalsInPreview(p: RecordPreview, names: ReadonlyMap<number, string>): RecordPreview {
  return {
    ...p,
    effects: Object.fromEntries(Object.entries(p.effects).map(([code, e]) => [code, { ...e, label: namedRemoval(e.label, names) }])),
    warnings: p.warnings.map((w) => namedRemoval(w, names)),
  };
}

/** A save's answer with the removal sentences named: its warnings and follow-ups (the
 *  receipt's lines). */
export function namedRemovalsInResult(r: RecordApplyResult, names: ReadonlyMap<number, string>): RecordApplyResult {
  return {
    ...r,
    warnings: r.warnings.map((w) => namedRemoval(w, names)),
    follow_ups: r.follow_ups.map((f) => ({ ...f, label: namedRemoval(f.label, names) })),
  };
}

/** What the card's live region — and a refused save — says of the days brought back. */
export function removedDaysDrifted(names: readonly string[]): string {
  const one = names.length === 1;
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)!}` : (names[0] ?? 'A day');
  return (
    `${list} changed on the server since you removed ${one ? 'it' : 'them'}: ${one ? 'it is' : 'they are'} back as saved now. ` +
    `Remove ${one ? 'it' : 'them'} again to delete ${one ? 'it' : 'them'}.`
  );
}
